import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, Modal } from 'reactstrap';
import { get, post, put, del } from 'helpers/api_helper';
import Datatables from 'pages/utils/table/datatable';
import { ManageChairsColumns } from './tableColumns';
import { CHAIR_URL } from 'helpers/url_helper';
import { showSuccessAlert, showConfirmAlert } from "pages/utils/alertMessages";
import { AvForm, AvField } from 'availity-reactstrap-validation';
import { useTranslation } from 'react-i18next';

import cookieHelper from 'helpers/getCookieData';

const timesSlots = [
    { label: "07:00", value: "07:00" },
    { label: "07:30", value: "07:30" },
    { label: "08:00", value: "08:00" },
    { label: "08:30", value: "08:30" },
    { label: "09:00", value: "09:00" },
    { label: "09:30", value: "09:30" },
    { label: "10:00", value: "10:00" },
    { label: "10:30", value: "10:30" },
    { label: "11:00", value: "11:00" },
    { label: "11:30", value: "11:30" },
    { label: "12:00", value: "12:00" },
    { label: "12:30", value: "12:30" },
    { label: "13:00", value: "13:00" },
    { label: "13:30", value: "13:30" },
    { label: "14:00", value: "14:00" },
    { label: "14:30", value: "14:30" },
    { label: "15:00", value: "15:00" },
    { label: "15:30", value: "15:30" },
    { label: "16:00", value: "16:00" },
    { label: "16:30", value: "16:30" },
    { label: "17:00", value: "17:00" },
    { label: "17:30", value: "17:30" },
    { label: "18:00", value: "18:00" },
    { label: "18:30", value: "18:30" },
    { label: "19:00", value: "19:00" },
    { label: "19:30", value: "19:30" },
    { label: "20:00", value: "20:00" },
    { label: "20:30", value: "20:30" },
    { label: "21:00", value: "21:00" },
    { label: "21:30", value: "21:30" },
    { label: "22:00", value: "22:00" },
    { label: "22:30", value: "22:30" },
    { label: "23:00", value: "23:00" },
    { label: "23:30", value: "23:30" }
];

const ManageChairs = () => {
    const { t } = useTranslation();
    const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;
    const [isForm, setIsForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [rows, setRows] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, Search: '' });

    const fetchData = async () => {
        setLoading(true);
        const clinicParam = clinic?.id ? `&clinic_id=${clinic.id}` : '';
        const { success, body } = await get(`${CHAIR_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.Search}${clinicParam}`);
        if (success) {
            setRows(body);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [pagination]);

    const handleToggle = () => {
        setIsForm(!isForm);
        setEditMode(false);
        setEditingId(null);
        setFormData(clinic?.id ? { clinic_id: clinic.id, intervel: 30, start_time: "09:00", end_time: "20:00" } : { intervel: 30 });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        const url = editMode ? `${CHAIR_URL}/${editingId}` : CHAIR_URL;
        const method = editMode ? put : post;
        const payload = {
            ...formData,
            clinic_id: formData.clinic_id || clinic?.id,
            cabin_no: parseInt(formData.cabin_no, 10) || 1,
            intervel: parseInt(formData.intervel, 10) || 30,
        };

        const { success } = await method(url, payload);

        if (success) {
            showSuccessAlert(editMode ? t('Chair updated successfully!') : t('Chair created successfully!'));
            setIsForm(false);
            setFormData({});
            setEditMode(false);
            setEditingId(null);
            fetchData();
        }
    };

    const handleInvalidSubmit = () => {
        alert(t("Please enter all required fields correctly."));
    };

    const handleEdit = (row) => {
        setFormData(row);
        setEditMode(true);
        setEditingId(row.id);
        setIsForm(true);
        setLoading(false);
    };

    const handleDelete = async (id, sts) => {
        const action = sts === 1 ? t('De-activate') : t('Activate');

        const { isConfirmed } = await showConfirmAlert(`${action} ${t('Chair')}`, `${t('Do you really want to')} ${action} ${t('this Chair')}?`,
            `${t('Yes')}, ${action} ${t('it')}!`, `${action}d!`, `${t('Chair has been updated successfully.')}`);

        if (isConfirmed) {
            const { success } = await del(`${CHAIR_URL}/${id}?sts=${sts}`);
            if (success) {
                fetchData();
            }
        }
    };

    return (
        <React.Fragment>
            <Card>
                <CardBody>
                    <Datatables
                        isSearch={true}
                        columns={ManageChairsColumns({ "edit": handleEdit, "delete": handleDelete }, pagination)}
                        showTableOnly={true}
                        rowsLength={rows?.totalItems || 0}
                        rows={rows.items || []}
                        keyField={'id'}
                        handleAddButton={handleToggle}
                        title={t("All Dental Operatory Chairs")}
                        isAdd={true}
                        isTableHead={true}
                        loading={loading}
                        ssr={setPagination}
                    />
                </CardBody>
            </Card>

            <Modal isOpen={isForm} toggle={handleToggle} size="xl" centered>
                <AvForm onValidSubmit={handleSubmit} onInvalidSubmit={handleInvalidSubmit} model={formData}>
                    <div className="modal-header">
                        <h5 className="modal-title font-weight-bold">
                            {editMode ? t('Update Dental Operatory Chair') : t('Create Dental Operatory Chair')}
                        </h5>
                        <button type="button" className="btn-close" onClick={handleToggle} aria-label="Close" />
                    </div>
                    <div className="modal-body p-4">
                        <Row className="g-3">
                            <Col md={4}>
                                <div className="mb-2">
                                    <AvField name="title" label={t("Chair Name / Label *")} type="text" placeholder={t("e.g. Operatory 1 (Main Chair)")} value={formData.title || ''} onChange={handleChange} required errorMessage={t("Please enter Chair Title")} />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <AvField type="select" name="start_time" label={t("Operating Start Time *")} value={formData.start_time || ''} onChange={handleChange} required errorMessage={t("Please select Start Time")}>
                                        <option value="">{t("Select Start Time")}</option>
                                        {timesSlots.map((time) => (
                                            <option key={time.value} value={time.value}>{time.label}</option>
                                        ))}
                                    </AvField>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <AvField name="end_time" label={t("Operating End Time *")} type="select" value={formData.end_time || ''} onChange={handleChange} required errorMessage={t("Please enter End Time")}>
                                        <option value="">{t("Select End Time")}</option>
                                        {timesSlots.map((time) => (
                                            <option key={time.value} value={time.value}>{time.label}</option>
                                        ))}
                                    </AvField>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <AvField name="cabin_no" label={t("Cabin / Room No *")} type="text" placeholder={t("e.g. Cabin A-101")} value={formData.cabin_no || ''} onChange={handleChange} required errorMessage={t("Please enter Cabin No")} />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <AvField name="intervel" label={t("Appointment Interval (Mins) *")} type="text" placeholder={t("e.g. 15 or 30")} value={formData.intervel || ''} onChange={handleChange} required errorMessage={t("Please enter Interval")} />
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="mb-2">
                                    <label className="form-label font-weight-semibold">{t("Description / Notes")}</label>
                                    <input type="text" className="form-control" placeholder={t("e.g. Fitted with apex locator")} name="description" value={formData.description || ''} onChange={handleChange} />
                                </div>
                            </Col>
                        </Row>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-danger" onClick={handleToggle}>
                            {t("Cancel")}
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? t('Processing...') : (editMode ? t('Update Chair') : t('Save Chair'))}
                        </button>
                    </div>
                </AvForm>
            </Modal>
        </React.Fragment>
    );
};

export default ManageChairs;
